/* eslint-disable */
import { useState, useEffect, useMemo } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MdArrowBack } from 'react-icons/md';
import { SuccessButton } from 'components/button/Success';
import { DangerButton } from 'components/button/Danger';
import { Button } from 'components/button';
import * as Dialog from '@radix-ui/react-dialog';
import { txsService } from 'views/services/txsService';
import { adminService } from 'views/services/adminService';
import {
  EEngineRunStatus,
  IDepositExtended,
} from 'views/types/typeTransaction';
import { toast } from 'App';
import {
  ReadPermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';
import SwapDetails from '../types/SwapDetails';
import ReferralDetails from '../types/ReferralDetails';
import CardDetails from '../types/CardDetails';
import DepositWithdrawDetails from '../types/DepositWithdrawDetails';
import InputField from 'components/fields/InputField';
import {
  EOrderStatus,
  EOrderType,
  ORDER_STATUS_OPTIONS,
} from 'views/types/transactions';
import Card from 'components/card';
import { MdClose } from 'react-icons/md';
import { errorToText } from 'utils/error-to-text';
import { statusToText } from 'utils/status-to-text';

type TransactionType =
  | 'deposit'
  | 'withdraw'
  | 'swap'
  | 'card_top_up'
  | 'card_withdraw'
  | 'card_issue'
  | 'card_transaction'
  | 'referral_transaction'
  | 'internal';

export interface Transaction extends IDepositExtended {
  type: TransactionType;
}

interface IProps {
  transaction: IDepositExtended;
  tx?: Transaction;
  onClose?: () => void;
  showActions?: boolean;
  isInternal?: boolean;
  isBlocked?: boolean;
  isWithdraw?: boolean;
  isSwap?: boolean;
  isCardTransaction?: boolean;
  isReferralTransaction?: boolean;
  onRefetch?: () => Promise<unknown>;
  onBack?: () => void;
}

export const TransactionDetails: React.FC<IProps> = ({
  transaction,
  tx,
  showActions = false,
  isInternal = false,
  isBlocked = false,
  isWithdraw = false,
  isSwap = false,
  isCardTransaction = false,
  isReferralTransaction = false,
  onRefetch,
  onBack,
}) => {
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const [isProcessed, setIsProcessed] = useState(!!transaction.processedAt);
  const currentTransaction = tx || transaction;
  const [refundAddress, setRefundAddress] = useState<string | undefined>();
  const { canUpdate, canRead } = usePermissions();
  const [isRestartModalOpen, setIsRestartModalOpen] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [restartError, setRestartError] = useState<string | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<EOrderStatus | ''>('');
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [changeStatusError, setChangeStatusError] = useState<string | null>(
    null
  );

  const getRestartedTransactions = (): string[] => {
    try {
      const stored = sessionStorage.getItem('restartedTransactions');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const addRestartedTransaction = (transactionId: string) => {
    try {
      const restarted = getRestartedTransactions();
      if (!restarted.includes(transactionId)) {
        restarted.push(transactionId);
        sessionStorage.setItem(
          'restartedTransactions',
          JSON.stringify(restarted)
        );
      }
    } catch {
      console.error('Error adding restarted transaction to sessionStorage');
    }
  };

  const removeRestartedTransaction = (transactionId: string) => {
    try {
      const restarted = getRestartedTransactions().filter(
        id => id !== transactionId
      );
      sessionStorage.setItem(
        'restartedTransactions',
        JSON.stringify(restarted)
      );
    } catch {
      console.error('Error removing restarted transaction from sessionStorage');
    }
  };

  const isTransactionRestarted = (transactionId: string): boolean => {
    return getRestartedTransactions().includes(transactionId);
  };

  const [hasRestartClicked, setHasRestartClicked] = useState(() =>
    isTransactionRestarted(transaction.id)
  );

  useEffect(() => {
    const completedStatuses = ['Completed', 'Success', 'Approved'];
    if (completedStatuses.includes(transaction.status)) {
      setHasRestartClicked(true);
      addRestartedTransaction(transaction.id);
    } else if (
      transaction?.engineRun?.status &&
      [EEngineRunStatus.FAILED].includes(transaction?.engineRun?.status)
    ) {
      setHasRestartClicked(false);
      removeRestartedTransaction(transaction.id);
    }
  }, [transaction.status, transaction.id]);

  const showActionButtons =
    ['deposit', 'withdraw'].includes(transaction.type) &&
    ['Rejected', 'Failed', 'Deposited', 'Amount Rejected', 'Blocked'].includes(
      currentTransaction?.status || ''
    );

  const handleApproveTransaction = async (id: string) => {
    if (
      !canUpdate(
        PERMISSION_RESOURCES.TRANSACTIONS_PAGE ||
          PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE ||
          PERMISSION_RESOURCES.APPROVE_CTA
      )
    ) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to approve this transaction',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsApproveLoading(true);
      if (isWithdraw || transaction.type.toLowerCase().includes('withdraw')) {
        await txsService.approveWithdraw(id);
      } else {
        await txsService.approveTransaction(id);
      }

      toast({
        title: 'Success',
        description: 'Transaction was approved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      if (onRefetch) {
        await onRefetch();
      }
      setIsProcessed(true);
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setIsApproveLoading(false);
    }
  };

  const handleRejectTransaction = async (id: string) => {
    if (
      !canUpdate(
        PERMISSION_RESOURCES.TRANSACTIONS_PAGE ||
          PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE ||
          PERMISSION_RESOURCES.REJECT_CTA
      )
    ) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to reject this transaction',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsRejectLoading(true);
      if (isWithdraw || transaction.type.toLowerCase().includes('withdraw')) {
        await txsService.rejectWithdraw(id);
      } else {
        await txsService.rejectTransaction(id, refundAddress);
        setRefundAddress(undefined);
      }
      toast({
        title: 'Success',
        description: 'Transaction was rejected successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      if (onRefetch) {
        await onRefetch();
      }
      setIsProcessed(true);
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setIsRejectLoading(false);
    }
  };

  const isAbleToApprove =
    ['Rejected', 'Failed', 'Deposited', 'Amount Rejected', 'Blocked'].includes(
      currentTransaction?.status || ''
    ) &&
    (currentTransaction?.user ||
      currentTransaction?.receiver ||
      currentTransaction?.sender);

  const handleRefundAddressChange = (value: string) => {
    if (
      !canUpdate(
        PERMISSION_RESOURCES.TRANSACTIONS_PAGE ||
          PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE ||
          PERMISSION_RESOURCES.WALLET_ADDRESS_INPUT
      )
    ) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to modify the refund address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setRefundAddress(value);
  };

  const isKycCompleted =
    currentTransaction?.user &&
    'is_kyc_verification_completed' in currentTransaction.user
      ? ((currentTransaction.user as any).is_kyc_verification_completed ?? true)
      : true;

  const getTransactionComponent = () => {
    const type = currentTransaction?.type?.toLowerCase() || '';

    if (isSwap || type.includes('swap')) {
      return <SwapDetails transaction={currentTransaction} />;
    }

    if (isReferralTransaction || type.includes('referral_transaction')) {
      return <ReferralDetails transaction={currentTransaction} />;
    }

    if (isCardTransaction || type.includes('card')) {
      return <CardDetails transaction={currentTransaction} />;
    }

    return (
      <DepositWithdrawDetails
        transaction={currentTransaction}
        isInternal={isInternal}
      />
    );
  };

  const isDeposit = transaction.type === EOrderType.DEPOSIT;
  const isWithdrawal = transaction.type === EOrderType.WITHDRAW;

  const transactionStatus = statusToText(transaction.status, transaction?.type);

  const canRestart =
    (isDeposit || isWithdrawal) &&
    (transactionStatus === 'Pending' ||
      transactionStatus === 'Failed' ||
      transactionStatus === 'Processing') &&
    !showActions;

  const canCancelWithdrawal =
    isWithdrawal &&
    (transactionStatus === 'Pending' ||
      transactionStatus === 'Failed' ||
      transactionStatus === 'Processing') &&
    transaction.engineRun?.status !== EEngineRunStatus.SUCCESS;

  const canCancelCardIssue =
    transaction.type === EOrderType.CARD_ISSUE &&
    (transactionStatus === 'Pending' ||
      transactionStatus === 'Failed' ||
      transactionStatus === 'Processing') &&
    transaction.engineRun?.status !== EEngineRunStatus.SUCCESS;

  const canCancelCardTopUp =
    transaction.type === EOrderType.CARD_TOP_UP &&
    (transactionStatus === 'Pending' ||
      transactionStatus === 'Failed' ||
      transactionStatus === 'Processing') &&
    transaction.engineRun?.status !== EEngineRunStatus.SUCCESS;

  const canCancelSwap =
    (isSwap || transaction.type === EOrderType.SWAP) &&
    (transactionStatus === 'Pending' ||
      transactionStatus === 'Failed' ||
      transactionStatus === 'Processing');

  const canCancel =
    (canCancelWithdrawal ||
      canCancelCardIssue ||
      canCancelCardTopUp ||
      canCancelSwap) &&
    !showActions;

  const handleRestart = async () => {
    if (
      !canUpdate(
        PERMISSION_RESOURCES.TRANSACTIONS_PAGE ||
          PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE ||
          PERMISSION_RESOURCES.APPROVE_CTA
      )
    ) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to restart this transaction',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setHasRestartClicked(true);
    addRestartedTransaction(transaction.id);
    setIsRestarting(true);
    setRestartError(null);
    try {
      if (isDeposit) {
        await adminService.restartDeposit(
          transaction.transactionHash ||
            transaction.transactionInHash ||
            transaction.id
        );
      } else if (isWithdrawal) {
        await adminService.restartWithdrawal(transaction.id);
      }
      if (onRefetch) await onRefetch();
      setIsRestartModalOpen(false);
      toast({
        title: 'Success',
        description: 'Transaction restart initiated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      setRestartError('Something went wrong');
      const restarted = getRestartedTransactions().filter(
        id => id !== transaction.id
      );
      sessionStorage.setItem(
        'restartedTransactions',
        JSON.stringify(restarted)
      );
      setHasRestartClicked(false);
    } finally {
      setIsRestarting(false);
    }
  };

  const handleCancel = async () => {
    if (
      !canUpdate(
        PERMISSION_RESOURCES.TRANSACTIONS_PAGE ||
          PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE ||
          PERMISSION_RESOURCES.APPROVE_CTA
      )
    ) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to restart this transaction',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCancelling(true);
    setCancelError(null);

    try {
      await adminService.cancelOrder(transaction.id, transaction.type);

      if (onRefetch) await onRefetch();
      setIsCancelModalOpen(false);
      toast({
        title: 'Success',
        description: 'Transaction cancellation initiated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      setCancelError(errorToText(error) || 'Something went wrong');
      setIsCancelling(false);
    }
  };

  const handleChangeStatus = async () => {
    if (
      !canUpdate(
        PERMISSION_RESOURCES.TRANSACTIONS_PAGE ||
          PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE
      )
    ) {
      toast({
        title: 'Access Denied',
        description:
          'You do not have permission to change the status of this transaction',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedStatus || selectedStatus === EOrderStatus.ALL) {
      setChangeStatusError('Please select a valid status');
      return;
    }

    setIsChangingStatus(true);
    setChangeStatusError(null);

    try {
      await txsService.setCustomStatus(transaction.id, selectedStatus);

      if (onRefetch) await onRefetch();
      setIsChangeStatusModalOpen(false);
      setSelectedStatus('');
      toast({
        title: 'Success',
        description: 'Transaction status updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      setChangeStatusError(errorToText(error) || 'Something went wrong');
    } finally {
      setIsChangingStatus(false);
    }
  };

  return (
    <ReadPermissionGate resource={PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE}>
      <div className="scrollbar-hidden grow overflow-x-auto mt-5">
        {onBack && (
          <div className="mb-4">
            <Button
              onClick={onBack}
              className="flex items-center gap-2 bg-navy hover:bg-navy-300 text-gray-700 dark:hover:bg-navy-600 dark:text-white"
            >
              <MdArrowBack className="text-sm" />
              Back to Transactions
            </Button>
          </div>
        )}
        {getTransactionComponent()}
        <div className="flex gap-4 mt-4">
          {canRestart && (
            <ReadPermissionGate resource={PERMISSION_RESOURCES.APPROVE_CTA}>
              <Button
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setIsRestartModalOpen(true)}
                disabled={hasRestartClicked || isRestarting}
              >
                {hasRestartClicked ? 'Restart Initiated' : 'Restart'}
              </Button>
              <Dialog.Root
                open={isRestartModalOpen}
                onOpenChange={setIsRestartModalOpen}
                modal
              >
                <Dialog.Portal>
                  <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-[70] bg-gray-800 bg-opacity-50" />
                  <Dialog.Content
                    onInteractOutside={e => {
                      if ((e.target as HTMLElement).closest('.chakra-toast')) {
                        e.preventDefault();
                      }
                    }}
                    className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[80] max-h-[85vh] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white lg-max:px-2 p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none dark:bg-gray-800 dark:text-white"
                  >
                    <Dialog.Title className="sm-max:w-full sm-max:flex-col-reverse flex justify-center items-center relative text-center text-mauve12 mb-10 text-[17px] font-medium">
                      Confirmation
                    </Dialog.Title>
                    <Card
                      extra={'w-full sm:overflow-auto lg-max:px-1 px-6 pb-6'}
                    >
                      <div className="flex flex-col gap-6 py-4">
                        <p className="text-center">
                          Are you sure you want to restart this transaction?
                        </p>
                        <Button
                          onClick={handleRestart}
                          disabled={isRestarting || hasRestartClicked}
                          className="disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isRestarting
                            ? 'Restarting...'
                            : hasRestartClicked
                              ? 'Restart Initiated'
                              : 'Restart'}
                        </Button>
                        {restartError && (
                          <span className="text-red-600">{restartError}</span>
                        )}
                      </div>
                    </Card>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="text-violet11 hover:bg-violet4 focus:shadow-violet7 absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
                        aria-label="Close"
                      >
                        <MdClose />
                      </button>
                    </Dialog.Close>
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
            </ReadPermissionGate>
          )}
          {canCancel && (
            <ReadPermissionGate resource={PERMISSION_RESOURCES.APPROVE_CTA}>
              <Button
                className="ml-0 bg-red-500 hover:bg-red-600 text-white font-semibold px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setIsCancelModalOpen(true)}
                disabled={
                  isCancelling ||
                  transaction.engineRun?.status === EEngineRunStatus.CANCELED
                }
              >
                Cancel
              </Button>
              <Dialog.Root
                open={isCancelModalOpen}
                onOpenChange={setIsCancelModalOpen}
                modal
              >
                <Dialog.Portal>
                  <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-[70] bg-gray-800 bg-opacity-50" />
                  <Dialog.Content
                    onInteractOutside={e => {
                      if ((e.target as HTMLElement).closest('.chakra-toast')) {
                        e.preventDefault();
                      }
                    }}
                    className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[80] max-h-[85vh] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white lg-max:px-2 p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none dark:bg-gray-800 dark:text-white"
                  >
                    <Dialog.Title className="sm-max:w-full sm-max:flex-col-reverse flex justify-center items-center relative text-center text-mauve12 mb-10 text-[17px] font-medium"></Dialog.Title>
                    <Card
                      extra={'w-full sm:overflow-auto lg-max:px-1 px-6 pb-6'}
                    >
                      <div className="flex flex-col gap-6 py-4">
                        <p className="text-center">
                          Are you sure you want to cancel this transaction?
                        </p>
                        <Button
                          onClick={handleCancel}
                          disabled={isCancelling}
                          variant="danger"
                          className="disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isCancelling ? 'Cancelling...' : 'Cancel'}
                        </Button>
                        {cancelError && (
                          <span className="text-red-600">{cancelError}</span>
                        )}
                      </div>
                    </Card>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="text-violet11 hover:bg-violet4 focus:shadow-violet7 absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
                        aria-label="Close"
                      >
                        <MdClose />
                      </button>
                    </Dialog.Close>
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
            </ReadPermissionGate>
          )}
          <UpdatePermissionGate
            resource={PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE}
          >
            <Button
              className="bg-purple-500 hover:bg-purple-600 text-white font-semibold px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setIsChangeStatusModalOpen(true)}
              disabled={isChangingStatus}
            >
              Change Status
            </Button>
            <Dialog.Root
              open={isChangeStatusModalOpen}
              onOpenChange={open => {
                setIsChangeStatusModalOpen(open);
                if (!open) {
                  setSelectedStatus('');
                  setChangeStatusError(null);
                }
              }}
              modal
            >
              <Dialog.Portal>
                <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-[70] bg-gray-800 bg-opacity-50" />
                <Dialog.Content
                  onInteractOutside={e => {
                    if ((e.target as HTMLElement).closest('.chakra-toast')) {
                      e.preventDefault();
                    }
                  }}
                  className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[80] max-h-[85vh] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white lg-max:px-2 p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none dark:bg-gray-800 dark:text-white"
                >
                  <Dialog.Title className="sm-max:w-full sm-max:flex-col-reverse flex justify-center items-center relative text-center text-mauve12 mb-10 text-[17px] font-medium">
                    Change Status
                  </Dialog.Title>
                  <Card extra={'w-full sm:overflow-auto lg-max:px-1 px-6 pb-6'}>
                    <div className="flex flex-col gap-6 py-4">
                      <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                        Current status:{' '}
                        <span className="font-semibold">
                          {statusToText(transaction.status, transaction.type)}
                        </span>
                      </p>
                      <select
                        value={selectedStatus}
                        onChange={e =>
                          setSelectedStatus(e.target.value as EOrderStatus)
                        }
                        className="w-full rounded-lg border p-3 bg-white dark:bg-navy-900 text-navy-700 dark:text-white"
                      >
                        <option value="">Select new status</option>
                        {ORDER_STATUS_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <Button
                        onClick={handleChangeStatus}
                        disabled={isChangingStatus || !selectedStatus}
                        className="disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isChangingStatus ? 'Updating...' : 'Update Status'}
                      </Button>
                      {changeStatusError && (
                        <span className="text-red-600">{changeStatusError}</span>
                      )}
                    </div>
                  </Card>
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      className="text-violet11 hover:bg-violet4 focus:shadow-violet7 absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
                      aria-label="Close"
                    >
                      <MdClose />
                    </button>
                  </Dialog.Close>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </UpdatePermissionGate>
        </div>
        {showActionButtons && (
          <div className="flex items-end gap-3 mt-6">
            <ReadPermissionGate resource={PERMISSION_RESOURCES.APPROVE_CTA}>
              <SuccessButton
                disabled={
                  !isAbleToApprove ||
                  isProcessed ||
                  isRejectLoading ||
                  isApproveLoading ||
                  !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS_PAGE) ||
                  !canUpdate(PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE) ||
                  !canUpdate(PERMISSION_RESOURCES.APPROVE_CTA)
                }
                onClick={() => {
                  handleApproveTransaction(currentTransaction.id);
                }}
                className={`w-[84px] h-[40px] justify-center disabled:opacity-50 disabled:cursor-not-allowed ${
                  isProcessed ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isApproveLoading ? (
                  <AiOutlineLoading3Quarters className="animate-spin" />
                ) : (
                  'Approve'
                )}
              </SuccessButton>
            </ReadPermissionGate>
            <ReadPermissionGate resource={PERMISSION_RESOURCES.REJECT_CTA}>
              <DangerButton
                disabled={
                  isProcessed ||
                  isApproveLoading ||
                  isRejectLoading ||
                  !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS_PAGE) ||
                  !canUpdate(PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE) ||
                  !canUpdate(PERMISSION_RESOURCES.REJECT_CTA)
                }
                onClick={() => {
                  handleRejectTransaction(currentTransaction.id);
                }}
                className={`w-[84px] h-[40px] justify-center disabled:opacity-50 disabled:cursor-not-allowed ${
                  isProcessed ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isRejectLoading ? (
                  <AiOutlineLoading3Quarters className="animate-spin" />
                ) : (
                  'Reject'
                )}
              </DangerButton>
            </ReadPermissionGate>
            {currentTransaction.type === 'deposit' &&
              canRead(PERMISSION_RESOURCES.WALLET_ADDRESS_INPUT) && (
                <InputField
                  id={''}
                  extra=""
                  label="Refund Address"
                  tooltip={
                    <div className="max-w-[18rem] text-gray-800 dark:text-gray-300">
                      If the transaction is <b>approved</b>, the refund address
                      will be ignored. Flow will be processed as usual.
                      <br />
                      <br />
                      Refund address will be used to send funds back to the user
                      if the transaction is <b>rejected</b>.
                      <br />
                      <br />
                      Refund address must be an <i>external address</i>. All
                      refunds to addresses from clients' pool will be failed.
                    </div>
                  }
                  extraClass={'min-w-96 h-[40px] py-0 !m-0'}
                  placeholder={'Refund address (or leave empty for default)'}
                  variant={'auth'}
                  value={refundAddress}
                  onChange={e => handleRefundAddressChange(e.target.value)}
                  disabled={
                    isProcessed ||
                    isApproveLoading ||
                    isRejectLoading ||
                    !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS_PAGE) ||
                    !canUpdate(PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE) ||
                    !canUpdate(PERMISSION_RESOURCES.WALLET_ADDRESS_INPUT)
                  }
                />
              )}
          </div>
        )}
      </div>
    </ReadPermissionGate>
  );
};

export default TransactionDetails;
